import React from 'react';
import { withAllContexts } from '../AllContextsWrapper';
import { Icon } from './Icons';
import {
  CalendarViewWeekOutlined as CalendarViewWeekOutlinedIcon,
  CalendarViewMonthOutlined as CalendarViewMonthOutlinedIcon,
  CalendarViewDayOutlined as CalendarViewDayOutlinedIcon,
  CalendarMonthOutlined as CalendarMonthOutlinedIcon,
  ViewWeekOutlined as ViewWeekOutlinedIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  RecentActorsOutlined as RecentActorsOutlinedIcon,
  RefreshOutlined as RefreshOutlinedIcon,
  SettingsOutlined as SettingsIcon,
} from "@mui/icons-material";
import { HookSlot } from '../hooks/usePluginHooks';

class SideMenu extends React.Component {
  closeSideMenu = () => {
    this.props.app.functions.setShownComponent(null);
  }

  hasAnalyticsAccess = (user) => {
    if (!user || !user.access) return false;
    const { activeCalendar } = this.props.app.state;
    if (!activeCalendar) return false;
    
    return user.access.some(acc => 
      (acc.role === 'admin' || acc.role === 'owner') && 
      (acc.calendarId === activeCalendar.id || acc.companyId === user.activeCompanyId)
    );
  }

  render() {
    const user = this.props.socketIO.user;
    let userImage = user?.image ? '/avatars/' + user.image : '/avatars/default.png';
    const { activeCalendar } = this.props.app.state;
    const isCamping = activeCalendar?.type === 'accommodation';
    const isOpen = this.props.app.state.shownComponent === 'sideMenu';
    
    // Theme-specific colors
    const sidebarHeaderClass = isCamping 
      ? "bg-taborisko-pine border-b border-taborisko-orange/20" 
      : "bg-splavma-teal border-b border-splavma-orange/20";
      
    const activeItemBg = isCamping ? "bg-taborisko-beige text-taborisko-pine" : "bg-splavma-teal/10 text-splavma-teal";
    const activeIconColor = isCamping ? "text-taborisko-orange" : "text-splavma-orange";

    return (
      <>
        {/* Backdrop */}
        {isOpen && (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 animate-fade-in"
            onClick={this.closeSideMenu}
          />
        )}

        {/* Sidebar */}
        <div className={`
          fixed top-0 left-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl z-50 flex flex-col
          transform transition-transform duration-500 cubic-bezier(0.4, 0, 0.2, 1)
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isCamping ? 'bg-taborisko-beige' : 'bg-white'}
        `}>
          {/* User Profile Section */}
          <div className={`${sidebarHeaderClass} p-8 text-white relative overflow-hidden group`}>
            {/* Background Decoration */}
            <div className="absolute -right-8 -top-8 w-32 h-32 bg-white/5 rounded-full blur-2xl group-hover:bg-white/10 transition-colors duration-500"></div>
            
            <HookSlot location="sidebar.top" />

            <div className="flex flex-col items-center space-y-5 relative z-10">
              <div className="relative">
                <div className={`absolute inset-0 rounded-full blur-md opacity-50 ${isCamping ? 'bg-taborisko-orange' : 'bg-splavma-orange'}`}></div>
                <img
                  src={userImage}
                  alt={user?.name}
                  className="w-24 h-24 rounded-full border-4 border-white/20 shadow-2xl object-cover relative z-10"
                  onError={(e) => { e.target.src = '/avatars/default.png' }}
                />
                <div className={`absolute bottom-1 right-1 w-6 h-6 rounded-full border-4 border-white z-20 ${isCamping ? 'bg-taborisko-gold' : 'bg-success-500'}`}></div>
              </div>

              <div className="text-center">
                <h3 className="text-xl font-extrabold tracking-tight">{user?.name || 'Užívateľ'}</h3>
                <p className="text-xs font-medium text-white/60 uppercase tracking-widest mt-1">{user?.email || ''}</p>
              </div>
            </div>
          </div>

          {/* Menu Content - Scrollable */}
          <div className="flex-1 overflow-y-auto py-6 scrollbar-hide">
            <HookSlot location="sidebar.menu.before" />

            {/* View Type Section */}
            <div className="px-6 mb-8">
              <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-4 px-4">Zobrazenie</h4>
              <div className="space-y-1">
                {this.renderMenuItem(
                  'Mesiac',
                  <CalendarMonthOutlinedIcon />,
                  () => { this.props.app.functions.setViewType('month'); localStorage.setItem('view', 'month'); },
                  this.props.app.state.view === 'month',
                  '',
                  activeItemBg,
                  activeIconColor
                )}
                {this.renderMenuItem(
                  'Dva týždne',
                  <CalendarViewWeekOutlinedIcon />,
                  () => { this.props.app.functions.setViewType('2weeks'); localStorage.setItem('view', '2weeks'); },
                  this.props.app.state.view === '2weeks',
                  '',
                  activeItemBg,
                  activeIconColor
                )}
                {this.renderMenuItem(
                  'Týždeň',
                  <CalendarViewDayOutlinedIcon />,
                  () => { this.props.app.functions.setViewType('week'); localStorage.setItem('view', 'week'); },
                  this.props.app.state.view === 'week',
                  '',
                  activeItemBg,
                  activeIconColor
                )}
                {this.renderMenuItem(
                  'Tri dni',
                  <ViewWeekOutlinedIcon />,
                  () => { this.props.app.functions.setViewType('3days'); localStorage.setItem('view', '3days'); },
                  this.props.app.state.view === '3days',
                  '',
                  activeItemBg,
                  activeIconColor
                )}
                {this.renderMenuItem(
                  'Deň',
                  <CalendarViewMonthOutlinedIcon />,
                  () => { this.props.app.functions.setViewType('1day'); localStorage.setItem('view', '1day'); },
                  this.props.app.state.view === '1day',
                  '',
                  activeItemBg,
                  activeIconColor
                )}
              </div>
            </div>

            {/* Calendars Section */}
            <div className="px-6 mb-8">
              <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-4 px-4">Kalendáre</h4>
              <div className="space-y-1">
                {this.props.app.state.calendars.map((calendar) =>
                  this.renderMenuItem(
                    calendar.name,
                    <CalendarMonthOutlinedIcon />,
                    () => {
                      this.props.app.functions.setCalendar(calendar.id);
                      this.closeSideMenu();
                    },
                    this.props.app.state.activeCalendar?.id === calendar.id,
                    '',
                    activeItemBg,
                    activeIconColor
                  )
                )}
              </div>
            </div>

            {/* Actions Section */}
            <div className="px-6 mb-8">
              <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-4 px-4">Akcie</h4>
              <div className="space-y-1">
                {this.renderMenuItem(
                  'Nová rezervácia',
                  <AddCircleOutlineIcon />,
                  () => this.props.app.functions.toggleAddNewReservation(),
                  false,
                  '',
                  activeItemBg,
                  activeIconColor
                )}
                {this.renderMenuItem(
                  'Klienti',
                  <RecentActorsOutlinedIcon />,
                  () => this.props.app.functions.setShownComponent('clients', true),
                  false,
                  '',
                  activeItemBg,
                  activeIconColor
                )}
              </div>
            </div>

            <HookSlot location="sidebar.menu.after" />

            {/* System Section */}
            <div className="px-6 mb-10">
              <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-4 px-4">Systém</h4>
              <div className="space-y-1">
                {user?.id === 1 && this.renderMenuItem(
                  'Administrácia',
                  <Icon icon="user-cog" className="text-xl" />,
                  () => {
                    this.props.app.functions.setShownComponent('admin', true);
                    this.closeSideMenu();
                  },
                  this.props.app.state.shownComponent === 'admin',
                  '',
                  activeItemBg,
                  activeIconColor
                )}
                {(user?.id === 1 || this.hasAnalyticsAccess(user)) && this.renderMenuItem(
                  'Analytika',
                  <Icon icon="chart-bar" className="text-xl" />,
                  () => {
                    this.props.app.functions.setShownComponent('analytics', true);
                    this.closeSideMenu();
                  },
                  this.props.app.state.shownComponent === 'analytics',
                  '',
                  activeItemBg,
                  activeIconColor
                )}
                {this.renderMenuItem(
                  'Znova načítať',
                  <RefreshOutlinedIcon />,
                  () => window.location.reload(),
                  false,
                  '',
                  activeItemBg,
                  activeIconColor
                )}
                {window.isFlutterApp && this.renderMenuItem(
                  'Nastavenia volaní',
                  <SettingsIcon />,
                  () => {
                    if (window.FlutterApp) {
                      window.FlutterApp.postMessage('open_settings');
                    }
                  },
                  false,
                  '',
                  activeItemBg,
                  activeIconColor
                )}
                {this.renderMenuItem(
                  'Odhlásiť sa',
                  <Icon icon="sign-out-alt" className="text-xl" />,
                  () => {
                    if (window.FlutterApp) {
                      window.FlutterApp.postMessage('logout');
                    } else {
                      window.location.href = '/api/logout';
                    }
                  },
                  false,
                  'text-red-500 hover:bg-red-50',
                  activeItemBg,
                  activeIconColor
                )}
              </div>
            </div>

            <HookSlot location="sidebar.bottom" />
          </div>
        </div>
      </>
    );
  }

  renderMenuItem(label, icon, onClick, isActive, customClass = '', activeBgClass = '', activeIconClass = '') {
    return (
      <button
        key={label}
        onClick={onClick}
        className={`
          w-full flex items-center gap-4 px-6 py-3 rounded-full text-left
          transition-all duration-300 group
          ${isActive
            ? `${activeBgClass} font-bold shadow-md ring-1 ring-black/5 scale-[1.02]`
            : 'text-gray-600 hover:bg-gray-50 hover:translate-x-1'
          }
          ${customClass}
        `}
      >
        <span className={`transition-colors duration-300 ${isActive ? activeIconClass : 'text-gray-400 group-hover:text-gray-600'}`}>
          {icon}
        </span>
        <span className="flex-1 text-sm tracking-tight">{label}</span>
      </button>
    );
  }


}

export default withAllContexts(SideMenu);
